import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Avatar,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Add, 
  Visibility, 
  Edit, 
  Delete,
  PlayArrow,
  Stop,
  CheckCircle,
  Store,
  TrendingUp,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { marketAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const StatCard = ({ title, value, icon, color, subtitle }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <Card
      sx={{
        background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`,
        border: `1px solid ${color}30`,
        borderRadius: 3,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="text.secondary" variant="body2" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', color }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: `${color}30`, color, width: 60, height: 60 }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  </motion.div>
);

const MarketManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [markets, setMarkets] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    available: 0,
    running: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMarkets();
  }, []);

  const fetchMarkets = async () => {
    try {
      setLoading(true);
      const response = await marketAPI.getMarkets();
      setMarkets(response.data.markets);
      setStats(response.data.stats);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to fetch markets');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMarket = async (id) => {
    if (window.confirm('Are you sure you want to delete this market?')) {
      try {
        await marketAPI.deleteMarket(id);
        fetchMarkets();
      } catch (error) {
        setError(error.response?.data?.message || 'Failed to delete market');
      }
    }
  };

  const getMarketStatus = (market) => {
    const now = new Date();
    const currentTime = now.toTimeString().split(' ')[0];
    
    if (market.market_status !== 'active') return { label: 'Inactive', color: 'error' };
    if (currentTime >= market.open_time && currentTime <= market.close_time) {
      return { label: 'Running', color: 'success' };
    }
    return { label: 'Closed', color: 'warning' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const columns = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 70,
      renderCell: (params) => (
        <Chip label={params.value} variant="outlined" size="small" />
      )
    },
    { 
      field: 'market_name', 
      headerName: 'Market Name', 
      width: 200, 
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'timing',
      headerName: 'Timing',
      width: 180,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">
            {params.row.open_time} - {params.row.close_time}
          </Typography>
        </Box>
      ),
    },
    
    {
      field: 'market_status',
      headerName: 'Market Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value?.toUpperCase()}
          color={params.value === 'active' ? 'success' : 'error'}
          variant="outlined"
          size="small"
        />
      ),
    },
    {
      field: 'market_available',
      headerName: 'Available',
      width: 100,
      renderCell: (params) => (
        params.value ? 
          <CheckCircle color="success" /> : 
          <Stop color="error" />
      ),
    },
    {
      field: 'createdAt', // Fixed: use createdAt instead of created_at
      headerName: 'Created',
      width: 120,
      renderCell: (params) => formatDate(params.value)
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton 
              size="small" 
              onClick={() => navigate(`/dashboard/markets/${params.row.id}`)}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
          {user.user_type === 'owner' && (
            <Tooltip title="Delete">
              <IconButton 
                size="small" 
                onClick={() => handleDeleteMarket(params.row.id)}
                color="error"
              >
                <Delete />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 3, 
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
          color: 'white',
          borderRadius: 3
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" gutterBottom>
              Market Management
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Manage all markets and their settings
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={<Add />}
            onClick={() => navigate('/dashboard/markets/create')}
            sx={{ 
              bgcolor: 'rgba(255,255,255,0.2)', 
              borderRadius: 3,
              px: 3,
              py: 1.5,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
            }}
          >
            Create New Market
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Markets"
            value={stats.total}
            subtitle="All markets"
            icon={<Store sx={{ fontSize: 30 }} />}
            color="#6366f1"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Markets"
            value={stats.active}
            subtitle="Currently active"
            icon={<CheckCircle sx={{ fontSize: 30 }} />}
            color="#10b981"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Available Markets"
            value={stats.available}
            subtitle="Available for play"
            icon={<Visibility sx={{ fontSize: 30 }} />}
            color="#f59e0b"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Running Markets"
            value={stats.running}
            subtitle="Currently running"
            icon={<PlayArrow sx={{ fontSize: 30 }} />}
            color="#ef4444"
          />
        </Grid>
      </Grid>

      {/* Markets Table */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Markets ({markets.length})
          </Typography>
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={markets}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell': {
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: 1,
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default MarketManagement;
